import React, { useEffect, useState, useRef } from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * LinkPreviews — scans `text` for URLs and renders a compact "WhatsApp-style"
 * Open-Graph preview card for each. The raw URL is hidden by Linkify and the
 * card itself is the only visible reference to the link.
 *
 * Usage (drop in below the rendered comment/post/message text):
 *   <p><RenderMentions text={c.content} /></p>
 *   <LinkPreviews text={c.content} />
 *
 * We cap rendering to the first 2 URLs per text block so a paragraph full of
 * links can't blow up the thread visually.
 */

// Module-level cache so the same URL across many comments only fetches once.
const previewCache = new Map();
// Track in-flight requests so concurrent mounts don't trigger duplicates.
const inflight = new Map();

const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"')\]]+[^\s<>"')\].,;:!?])/gi;

function extractUrls(text) {
  if (!text) return [];
  const set = new Set();
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    const raw = m[0];
    const url = raw.startsWith('www.') ? `https://${raw}` : raw;
    set.add(url);
  }
  return Array.from(set).slice(0, 2);
}

function normalizeHost(url) {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return url; }
}

function PreviewCard({ url }) {
  const [meta, setMeta] = useState(() => previewCache.get(url) || null);
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (meta || failed) return;
    let cancelled = false;
    const run = async () => {
      try {
        let promise = inflight.get(url);
        if (!promise) {
          promise = fetch(`/api/forum/url-preview?url=${encodeURIComponent(url)}`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .finally(() => inflight.delete(url));
          inflight.set(url, promise);
        }
        const data = await promise;
        if (cancelled || !mountedRef.current) return;
        if (data && (data.title || data.description || data.image)) {
          previewCache.set(url, data);
          setMeta(data);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled && mountedRef.current) setFailed(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [url, meta, failed]);

  // Skeleton while loading
  if (!meta && !failed) {
    return (
      <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden bg-white animate-pulse flex" data-testid="link-preview-skeleton">
        <div className="w-20 h-20 bg-slate-100 flex-shrink-0" />
        <div className="p-3 flex-1 space-y-2">
          <div className="h-2.5 bg-slate-100 rounded w-1/3" />
          <div className="h-3 bg-slate-200 rounded w-3/4" />
          <div className="h-2.5 bg-slate-100 rounded w-1/2" />
        </div>
      </div>
    );
  }

  // Fallback (server couldn't scrape) — render a tiny chip with the domain
  if (failed || !meta) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-slate-50 border border-slate-200 text-slate-600 hover:border-brand-navy/40 hover:text-brand-navy transition-colors max-w-full"
        data-testid="link-preview-fallback">
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{normalizeHost(url)}</span>
      </a>
    );
  }

  // Absolute-ify internal images served by our backend
  const img = meta.image && meta.image.startsWith('/') ? meta.image : meta.image;
  const site = meta.site || normalizeHost(meta.url || url);
  const title = meta.title || normalizeHost(meta.url || url);

  return (
    <a href={meta.url || url} target="_blank" rel="noopener noreferrer"
      className="mt-2 border border-slate-200 rounded-lg overflow-hidden bg-white hover:border-brand-navy/40 hover:shadow-sm transition-all flex max-w-full"
      data-testid="link-preview-card">
      {img && (
        <img src={img} alt=""
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          className="w-20 sm:w-24 h-20 sm:h-24 object-cover flex-shrink-0 bg-slate-50" />
      )}
      <div className="p-3 flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{site}</p>
        <p className="text-sm font-semibold text-slate-800 leading-tight line-clamp-2 mt-0.5 break-words">{title}</p>
        {meta.description && (
          <p className="text-xs text-slate-500 line-clamp-2 mt-1 break-words">{meta.description}</p>
        )}
      </div>
    </a>
  );
}

export default function LinkPreviews({ text }) {
  const urls = extractUrls(text);
  if (urls.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="link-previews-container">
      {urls.map(u => <PreviewCard key={u} url={u} />)}
    </div>
  );
}
